import type React from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { LayoutGrid, Search, X } from "lucide-react"
import DropdownChevron from "../dropdown-chevron"

export const FORM_LABEL_CLASS = "text-paragraph font-bold text-gray-800"
export const FORM_INPUT_CLASS = "text-small"

export const SectionHeader = ({
    title,
    icon: Icon = LayoutGrid,
}: {
    title: string
    icon?: React.ComponentType<{ className?: string }>
}) => (
    <div className="bg-[#E6EDDD] p-4 flex items-center justify-between rounded-md mb-6">
        <div className="flex items-center gap-3">
            <Icon className="w-5 h-5 text-gray-700" />
            <h2 className="font-bold text-gray-800 tracking-tight">{title}</h2>
        </div>
    </div>
)

export const FormField = ({
    label,
    children,
    required,
}: {
    label: string
    children: React.ReactNode
    optional?: boolean
    required?: boolean
}) => (
    <div className="flex flex-col gap-1.5 mb-5">
        <label className={FORM_LABEL_CLASS}>
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        {children}
    </div>
)

export const MultiSelect = ({
    options,
    selected,
    onToggle,
    placeholder = "Select options",
    searchable = true,
}: {
    options: string[]
    selected: string[]
    onToggle: (val: string) => void
    placeholder?: string
    searchable?: boolean
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState("")
    const containerRef = useRef<HTMLDivElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)

    const closeDropdown = useCallback(() => {
        setIsOpen(false)
        setSearchQuery("")
    }, [])

    const filteredOptions = useMemo(() => {
        if (!searchable) return options
        const q = searchQuery.trim().toLowerCase()
        if (!q) return options
        return options.filter((option) => option.toLowerCase().includes(q))
    }, [options, searchQuery, searchable])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                closeDropdown()
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [closeDropdown])

    useEffect(() => {
        if (!isOpen || !searchable) return
        const id = window.requestAnimationFrame(() => {
            searchInputRef.current?.focus()
        })
        return () => window.cancelAnimationFrame(id)
    }, [isOpen, searchable])

    useEffect(() => {
        if (!isOpen || !searchable) return

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault()
                closeDropdown()
                return
            }

            const target = event.target as HTMLElement
            if (target === searchInputRef.current) return
            if (
                (target.tagName === "INPUT" || target.tagName === "TEXTAREA") &&
                !containerRef.current?.contains(target)
            ) {
                return
            }

            if (
                event.key.length === 1 &&
                !event.ctrlKey &&
                !event.metaKey &&
                !event.altKey
            ) {
                event.preventDefault()
                searchInputRef.current?.focus()
                setSearchQuery((prev) => prev + event.key)
            }
        }

        document.addEventListener("keydown", handleKeyDown)
        return () => document.removeEventListener("keydown", handleKeyDown)
    }, [isOpen, searchable, closeDropdown])

    return (
        <div className="relative" ref={containerRef}>
            <div
                className="relative flex items-center bg-[#F9F9F9] p-2 rounded-md gap-2 cursor-pointer min-h-[46px]"
                onClick={() => {
                    if (isOpen) closeDropdown()
                    else setIsOpen(true)
                }}
            >
                <div className="flex flex-wrap gap-2">
                    {selected.length > 0 ? (
                        selected.map((item) => (
                            <span
                                key={item}
                                className="bg-black text-white text-[11px] px-3 py-1.5 rounded-md font-medium flex items-center gap-1.5"
                            >
                                {item}
                                <X
                                    className="w-3 h-3 cursor-pointer"
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        onToggle(item)
                                    }}
                                />
                            </span>
                        ))
                    ) : (
                        <span className={`text-gray-400 ${FORM_INPUT_CLASS} ml-2`}>{placeholder}</span>
                    )}
                </div>
                <div className="flex-1"></div>
                <DropdownChevron isOpen={isOpen} />
            </div>

            {isOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden">
                    {searchable && (
                        <div
                            className="sticky top-0 z-10 border-b border-gray-200 bg-white p-2"
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <div className="relative">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder="Type to search..."
                                    className={`w-full rounded-md border border-gray-200 bg-[#F9F9F9] py-2 pl-9 pr-3 ${FORM_INPUT_CLASS} text-gray-800 placeholder:text-gray-400 focus:border-lime-400 focus:outline-none focus:ring-2 focus:ring-lime-400`}
                                    aria-label="Search options"
                                />
                            </div>
                        </div>
                    )}

                    <div className="max-h-60 overflow-y-auto">
                        {filteredOptions.length === 0 ? (
                            <div className={`px-4 py-3 ${FORM_INPUT_CLASS} text-gray-500`}>No results found</div>
                        ) : (
                            filteredOptions.map((option) => (
                                <div
                                    key={option}
                                    className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-[#ABD405] transition-colors ${selected.includes(option) ? "bg-[#ABD405]" : ""}`}
                                    onClick={() => onToggle(option)}
                                >
                                    <div
                                        className={`w-4 h-4 border rounded-md flex items-center justify-center ${selected.includes(option) ? "border-[#E6EDDD] bg-white" : "border-gray-300"}`}
                                    >
                                        {selected.includes(option) && (
                                            <div className="w-2 h-2 bg-[#E6EDDD] rounded-md" />
                                        )}
                                    </div>
                                    <span className={`${FORM_INPUT_CLASS} text-gray-700`}>{option}</span>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
